import { createRoute } from "@tanstack/react-router";
import { useState } from "react";
import { useTranslation } from "@/hooks/useSafeTranslation";
import { toast } from "@/lib/safe-toast";
import { Copy, Gift, Percent, Users } from "lucide-react";
import { authenticatedRoute } from "../_authenticated";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  Button,
  StatCard,
} from "@/components/ui";
import { cn, toErrorDisplayMessage, toDisplayString, toSafeReactNode } from "@/lib/utils";
import { useAuth } from "@/contexts/useAuth";
import { useReferrals } from "@/hooks/useReferrals";
import { trackEvent } from "@/api";

const DISCOUNT_TIERS = [
  { count: 5, discount: 20 },
  { count: 10, discount: 50 },
];

function ReferralsPage() {
  const { t, i18n } = useTranslation("common");
  const locale = i18n.language || "en";
  const [copied, setCopied] = useState(false);
  const { state } = useAuth();
  const token = state.status === "authenticated" ? state.token : null;
  const { data, isLoading, error } = useReferrals();
  const referralLink = data?.referralLink ?? null;
  const activePayingCount = data?.activePayingCount ?? 0;
  const rawDiscount = Number(data?.discountPercent);
  const discountPercent = Number.isFinite(rawDiscount) ? rawDiscount : 0;

  const copyLink = () => {
    if (!referralLink) return;
    trackEvent("referral_link_copied", token, {}).catch(() => {});
    navigator.clipboard.writeText(referralLink).then(
      () => {
        setCopied(true);
        toast.success(t("referrals.linkCopied"));
        setTimeout(() => setCopied(false), 2000);
      },
      () => toast.error(t("referrals.copyError"))
    );
  };

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-xl font-semibold tracking-tight text-text">{toSafeReactNode(t("referrals.title"))}</h1>
        <p className="text-sm text-text-muted">{toSafeReactNode(t("referrals.subtitle"))}</p>
      </div>

      {isLoading && <p className="text-sm text-text-muted">{toSafeReactNode(t("common.loading"))}</p>}
      {error && <p className="text-sm text-danger">{toErrorDisplayMessage(error)}</p>}

      {data && (
        <>
          <div className="grid gap-4 sm:grid-cols-3">
            <StatCard
              label={toDisplayString(t("referrals.payingReferrals"))}
              value={activePayingCount}
              icon={<Users className="h-4 w-4" />}
            />
            <StatCard
              label={toDisplayString(t("referrals.yourDiscount"))}
              value={`${discountPercent} %`}
              trend={toDisplayString(t("referrals.trendPermanent"))}
              icon={<Percent className="h-4 w-4" />}
            />
            <StatCard
              label={toDisplayString(t("referrals.totalReferred"))}
              value={data.referrals.length}
              icon={<Gift className="h-4 w-4" />}
            />
          </div>

          <Card>
            <CardHeader>
              <CardTitle>{toSafeReactNode(t("referrals.yourLink"))}</CardTitle>
              <CardDescription>{toSafeReactNode(t("referrals.yourLinkDescription"))}</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex gap-2">
                <code className="flex-1 rounded-md border border-border bg-surface-2 px-3 py-2 text-sm text-text break-all">
                  {toDisplayString(referralLink ?? t("referrals.noLink"))}
                </code>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={copyLink}
                  disabled={!referralLink}
                  className="shrink-0"
                >
                  <Copy className="mr-1 h-4 w-4" aria-hidden />
                  {copied ? toSafeReactNode(t("referrals.copied")) : toSafeReactNode(t("referrals.copy"))}
                </Button>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>{toSafeReactNode(t("referrals.discountTiers"))}</CardTitle>
              <CardDescription>{toSafeReactNode(t("referrals.discountTiersDescription"))}</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                {DISCOUNT_TIERS.map((tier) => {
                  const reached = activePayingCount >= tier.count;
                  const progress = Math.min(
                    (activePayingCount / tier.count) * 100,
                    100
                  );
                  return (
                    <div key={tier.count} className="space-y-1.5">
                      <div className="flex items-center justify-between text-sm">
                        <span
                          className={cn(
                            "font-medium",
                            reached ? "text-success" : "text-text"
                          )}
                        >
                          {toSafeReactNode(t("referrals.tierLabel", { count: tier.count, discount: tier.discount }))}
                        </span>
                        <span className="text-text-muted">
                          {activePayingCount} / {tier.count}
                        </span>
                      </div>
                      <div className="h-2 w-full overflow-hidden rounded-full bg-surface-2">
                        <div
                          className={cn(
                            "h-full rounded-full transition-all",
                            reached ? "bg-success" : "bg-accent"
                          )}
                          style={{ width: `${progress}%` }}
                        />
                      </div>
                    </div>
                  );
                })}
              </div>
              <p className="mt-3 text-xs text-text-muted">{toSafeReactNode(t("referrals.tierNote"))}</p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>{toSafeReactNode(t("referrals.history"))}</CardTitle>
              <CardDescription>{toSafeReactNode(t("referrals.historyDescription"))}</CardDescription>
            </CardHeader>
            <CardContent>
              {data.referrals.length === 0 ? (
                <p className="text-sm text-text-muted">{toSafeReactNode(t("referrals.noReferralsYet"))}</p>
              ) : (
                <ul className="space-y-2">
                  {data.referrals.map((item) => (
                    <li
                      key={`${toDisplayString(item.referredUserId)}-${toDisplayString(item.createdAt ?? "")}`}
                      className="flex items-center justify-between gap-2 rounded-md border border-border bg-surface-2 px-3 py-2 text-sm"
                    >
                      <span className="font-mono text-xs text-text-muted">
                        {toDisplayString(item.referredUserId).slice(0, 8)}…
                      </span>
                      <span className="text-text-muted">
                        {new Date(item.createdAt).toLocaleDateString(toDisplayString(locale))}
                      </span>
                      <span
                        className={cn(
                          "rounded-md px-2 py-0.5 text-xs font-medium",
                          item.hasActivePaidSubscription
                            ? "bg-success/20 text-success"
                            : "bg-surface text-text-muted"
                        )}
                      >
                        {item.hasActivePaidSubscription ? toSafeReactNode(t("referrals.paying")) : toSafeReactNode(t("referrals.notPaying"))}
                      </span>
                    </li>
                  ))}
                </ul>
              )}
            </CardContent>
          </Card>
        </>
      )}
    </div>
  );
}

export const referralsRoute = createRoute({
  getParentRoute: () => authenticatedRoute,
  path: "referrals",
  component: ReferralsPage,
});
